/**
 * Storage service for saved system prompts.
 *
 * Uses localStorage with a 30-minute TTL.
 */

import { ENV } from "../config";

const PROMPT_EXPIRY_MS = 30 * 60 * 1000;

interface PromptData {
  savedPrompt: string;
  userEmail: string;
  timestamp: number;
}

class StorageService {
  private static getStorageKey(userEmail: string): string {
    return `${ENV}-prompt-svc:${userEmail}`;
  }

  private static getData(userEmail: string): PromptData | null {
    try {
      const raw = localStorage.getItem(StorageService.getStorageKey(userEmail));
      if (!raw) return null;

      const data = JSON.parse(raw) as PromptData;

      if (Date.now() - data.timestamp > PROMPT_EXPIRY_MS) {
        localStorage.removeItem(StorageService.getStorageKey(userEmail));
        return null;
      }

      return data;
    } catch {
      return null;
    }
  }

  static async get(userEmail: string): Promise<string | null> {
    const data = StorageService.getData(userEmail);
    return data?.savedPrompt ?? null;
  }

  static async set(prompt: string, userEmail: string): Promise<void> {
    const data: PromptData = {
      savedPrompt: prompt,
      userEmail,
      timestamp: Date.now()
    };
    localStorage.setItem(StorageService.getStorageKey(userEmail), JSON.stringify(data));
  }

  static clear(userEmail: string): void {
    localStorage.removeItem(StorageService.getStorageKey(userEmail));
  }
}

export const storage = StorageService;
